import type { QuerySpec } from '@helios/powersync-client-core';

export type { PatientFieldDefinitionRow, EffectivePatientField } from './patient-form-logic';
export { buildPatientFormSettingsVM } from './patient-form-logic';
import type { EffectivePatientField } from './patient-form-logic';
import { MAX_REPORTED_AGE } from './patient-age-logic'; // PRD.315

/**
 * QuerySpec for fetching global override rows from patient_field_definitions.
 */
export function qPatientFieldDefinitionOverrides(): QuerySpec<[]> {
  return {
    key: ['patient-form', 'overrides'] as const,
    sql: `SELECT * FROM patient_field_definitions ORDER BY key`,
    params: [],
    watch: ['patient_field_definitions'],
  };
}

// ─── ID Type options (idType field config) ───

/**
 * Stored under `validation_override.idTypeOptions` for the `idType` field.
 * Lets a clinic decide which ID types appear in the Patient Form's "ID Type"
 * dropdown — both predefined entries can be toggled and custom entries can
 * be added.
 */
export interface IdTypeOptionsConfig {
  /** Ordered list of ID type labels presented to the user. */
  types: string[];
}

/** Defaults applied when a clinic hasn't customised the list yet. */
export const DEFAULT_ID_TYPES: string[] = [
  'National ID',
  'Passport',
  'Driver License',
  'SSN',
  'Other',
];

/** Predefined catalog the settings page surfaces as toggle-able presets.
 *  Note: 'Aadhaar' is intentionally omitted — it's withheld for legal reasons
 *  (see BLOCKED_ID_TYPE_KEYS in app-config.ts). */
export const PREDEFINED_ID_TYPES: string[] = [
  'National ID',
  'Passport',
  'Driver License',
  'SSN',
  'PAN',
  'Voter ID',
  'Health Card',
  'Insurance Card',
  'Other',
];

/** Parse the idTypeOptions config from a field's `validation_override` JSON. */
export function parseIdTypeOptions(
  validationOverride: string | null | undefined,
): IdTypeOptionsConfig {
  if (!validationOverride) return { types: DEFAULT_ID_TYPES };
  try {
    const parsed = JSON.parse(validationOverride) as { idTypeOptions?: unknown };
    const cfg = parsed?.idTypeOptions as { types?: unknown } | undefined;
    if (cfg && Array.isArray(cfg.types)) {
      const cleaned = cfg.types
        .filter((t): t is string => typeof t === 'string')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      if (cleaned.length > 0) return { types: cleaned };
    }
  } catch {
    /* ignore */
  }
  return { types: DEFAULT_ID_TYPES };
}

/**
 * Merge a new `idTypeOptions` config into an existing `validation_override`
 * JSON string, preserving any other keys (e.g. validation rules). Returns
 * the new JSON string, or null when the result is empty.
 */
export function writeIdTypeOptions(
  validationOverride: string | null,
  config: IdTypeOptionsConfig | null,
): string | null {
  let existing: Record<string, unknown> = {};
  if (validationOverride) {
    try {
      existing = JSON.parse(validationOverride) as Record<string, unknown>;
    } catch {
      /* fall through to empty */
    }
  }
  if (config) {
    existing.idTypeOptions = { types: config.types };
  } else {
    delete existing.idTypeOptions;
  }
  if (Object.keys(existing).length === 0) return null;
  return JSON.stringify(existing);
}

/**
 * PRD.315 — default rules for fields whose validity is a property of the field
 * itself rather than a clinic preference. A clinic `validation_override` still
 * wins, so this is a floor, not a lock.
 *
 * Reported age must be a whole number in a sane range: an empty box means "not
 * reported" (the validator skips empty answers), but `35.7` or `1990` is a
 * mistake that should be shown to the user rather than silently discarded at
 * the write boundary.
 */
const BUILT_IN_FIELD_VALIDATION: Record<string, unknown> = {
  age: {
    type: 'number',
    rules: [
      { kind: 'number.int', value: true, message: 'Age must be a whole number' },
      { kind: 'number.min', value: 0, message: 'Age cannot be negative' },
      {
        kind: 'number.max',
        value: MAX_REPORTED_AGE,
        message: `Age must be ${MAX_REPORTED_AGE} or less`,
      },
    ],
  },
};

/**
 * Builds a merged ValidationSchema from per-field validation_override strings.
 * Returns a JSON string suitable for IntakeRenderer's validationSchema prop,
 * or null if no fields have validation overrides.
 */
export function buildPatientValidationSchema(fields: EffectivePatientField[]): string | null {
  const fieldEntries: Record<string, unknown> = {};
  for (const f of fields) {
    if (!f.validation_override) {
      // No clinic override — fall back to a built-in rule set if this field has
      // one. Keeps validation in the EXISTING schema mechanism (the same rules
      // the IntakeRenderer already enforces and reports inline) instead of
      // bolting a second validation path onto one field.
      const builtIn = BUILT_IN_FIELD_VALIDATION[f.field_key];
      if (builtIn) fieldEntries[f.field_key] = builtIn;
      continue;
    }
    try {
      const parsed = JSON.parse(f.validation_override);
      if (parsed && Array.isArray(parsed.rules) && parsed.rules.length > 0) {
        fieldEntries[f.field_key] = parsed;
      }
    } catch {
      /* skip invalid JSON */
    }
  }
  if (Object.keys(fieldEntries).length === 0) return null;
  return JSON.stringify({ version: 1, fields: fieldEntries });
}
